import React from 'react';
import { NextPageContext } from 'next';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs from '../components/head/Breadcrumbs';
import Container from '../components/grid/Container';
import HorizontalLine from '../components/common/HorizontalLine';
import { CmsHelpPageCategory } from '../integrations/contentful/types';
import {
  fetchHelpPageCategories,
  fetchMainHelpPage
} from '../integrations/contentful/queries';
import BackButton from '../components/navigation/BackButton';
import ContentfulRichText from '../components/common/ContentfulRichText';
import ContactCard from '../components/help-pages/ContactCard';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import DisplayOn from '../components/common/DisplayOn';
import SmallHelpCategoryItem from '../components/help-pages/SmallHelpCategoryItem';
import StickyCol from '../components/grid/StickyCol';
import { columnTopSpacing } from '../styles/theme';
import { TimPage } from './TimPage';
import { helpPageBreadcrumbItem } from './HelpPage';

interface HelpCategoryPageProps {
  sideBarTitle?: string;
  helpCategory?: CmsHelpPageCategory;
  helpCategories: readonly CmsHelpPageCategory[];
}

const HelpCategoryPage: TimPage<HelpCategoryPageProps> = ({
  sideBarTitle,
  helpCategory,
  helpCategories
}) => {
  if (!helpCategory) {
    return null;
  }

  const canonicalPath = generatePath(Paths.HelpCategory, {
    slug: helpCategory.fields.slug
  });

  const topics = helpCategory.fields.topics;
  return (
    <div data-cy="help-category-page">
      <SEO
        title={helpCategory.fields.seoTitle || helpCategory.fields.title}
        canonicalPath={generatePath(Paths.Help)}
        description={
          helpCategory.fields.seoDescription ||
          helpCategory.fields.ingress ||
          ''
        }
      />
      <Breadcrumbs
        items={[
          helpPageBreadcrumbItem,
          {
            name: helpCategory.fields.seoTitle || helpCategory.fields.title,
            path: canonicalPath
          }
        ]}
      />
      <Container>
        <Row>
          <Col size={[24, 24, 7]}>
            <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
              <DisplayOn large>
                <Spacing bottom={5}>
                  <Spacing bottom={1}>
                    <H styling="h5" color="gray">
                      {t('Tietoa palvelusta')}
                    </H>
                  </Spacing>
                  <H styling="h2">
                    <strong>
                      {sideBarTitle ||
                        t('Mutkia matkassa? Tutustu aihealueisiin')}
                    </strong>
                  </H>
                </Spacing>
                <SmallHelpCategoryItem path={Paths.Help} text={t('Etusivu')} />
                {helpCategories.map((category) => {
                  return (
                    <Spacing top={1} key={category.sys.id}>
                      <SmallHelpCategoryItem
                        path={Paths.HelpCategory}
                        slug={category.fields.slug}
                        text={category.fields.title}
                        selected={
                          category.fields.slug === helpCategory.fields.slug
                        }
                      />
                    </Spacing>
                  );
                })}
              </DisplayOn>
            </StickyCol>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 16]}>
            <Spacing bottom={[6, 6, 4]} top={columnTopSpacing.contentColumn}>
              <DisplayOn small medium>
                <Spacing bottom={1}>
                  <H styling="h5" color="gray">
                    {helpCategory.fields.label}
                  </H>
                </Spacing>
              </DisplayOn>
              <H styling="h1">
                <strong>{helpCategory.fields.title}</strong>
              </H>
            </Spacing>
            <Spacing bottom={3} right={2}>
              <H styling="h3" tag="div">
                {helpCategory.fields.ingress}
              </H>
            </Spacing>
            <HorizontalLine />
            <Spacing top={5}>
              {topics.map((topic, index) => {
                const isLastTopic = index === topics.length - 1;
                return (
                  <div key={topic.sys.id} data-testid={'help-topic'}>
                    <Spacing right={[0, 13, 13]}>
                      <H styling="h3">
                        <strong>{topic.fields.title}</strong>
                      </H>
                      <Spacing top={2} right={2} bottom={-2}>
                        <ContentfulRichText
                          richTextDocument={topic.fields.content}
                        />
                      </Spacing>
                      {!isLastTopic && (
                        <Spacing bottom={5}>
                          <HorizontalLine />
                        </Spacing>
                      )}
                    </Spacing>
                  </div>
                );
              })}
            </Spacing>
            <Spacing top={11} bottom={18}>
              <ContactCard />
            </Spacing>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

HelpCategoryPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <BackButton
        defaultTitle={t('Tietoa palvelusta')}
        defaultPath={Paths.Help}
      />
    </DisplayOn>,
    page
  );

HelpCategoryPage.getInitialProps = async function ({ query }: NextPageContext) {
  const categorySlug = String(query.slug);
  const [[helpCategory], mainHelpPage] = await Promise.all([
    fetchHelpPageCategories(categorySlug),
    fetchMainHelpPage()
  ]);

  const helpCategories = mainHelpPage ? mainHelpPage.fields.categories : [];
  const sideBarTitle = mainHelpPage ? mainHelpPage.fields.title : undefined;
  const httpErrorCode = !helpCategory ? 404 : undefined;

  return {
    sideBarTitle,
    helpCategory,
    helpCategories,
    httpErrorCode
  };
};

export default HelpCategoryPage;
